/**
 * Wishlist functionality for Shopify theme
 */

class Wishlist {
  constructor() {
    this.storageKey = 'vanacore_wishlist';
    this.cacheKey = 'vanacore_wishlist_cache';
    this.wishlistData = this.getWishlist();
    this.productCache = this.getProductCache();
    this.initialized = false;
    this.isRendering = false; // Flag per prevenire ri-rendering durante operazioni
    this.renderedProducts = new Set(); // Track dei prodotti già renderizzati
    this.init();
  }

  init() {
    this.updateWishlistDisplay();
    this.bindEvents();
    this.initializeButtons();
    this.observeDOM();
    this.removeHeaderBadge();
    this.initialized = true;
  }

  // Inizializza i pulsanti con retry logic
  initializeButtons() {
    this.updateWishlistButtons();
    
    // Retry dopo brevi delay per catturare elementi caricati dinamicamente
    const retryTimes = [50, 100, 250, 500, 1000];
    
    retryTimes.forEach((delay) => {
      setTimeout(() => {
        this.updateWishlistButtons();
      }, delay);
    });
  }

  // Get wishlist from localStorage
  getWishlist() {
    try {
      const data = localStorage.getItem(this.storageKey);
      const parsed = JSON.parse(data) || [];
      return parsed;
    } catch (e) {
      console.error('Error parsing wishlist data:', e);
      return [];
    }
  }

  // Get product cache from localStorage
  getProductCache() {
    try {
      const cache = localStorage.getItem(this.cacheKey);
      const parsed = JSON.parse(cache) || {};
      // Clean cache older than 1 hour
      const oneHour = 60 * 60 * 1000;
      const now = Date.now();
      
      Object.keys(parsed).forEach(key => {
        if (parsed[key].timestamp < now - oneHour) {
          delete parsed[key];
        }
      });
      
      return parsed;
    } catch (e) {
      console.error('Error parsing product cache:', e);
      return {};
    }
  }

  // Save product cache to localStorage
  saveProductCache() {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify(this.productCache));
    } catch (e) {
      console.error('Error saving product cache:', e);
    }
  }

  // Add product to cache
  cacheProduct(productId, productData) {
    this.productCache[productId] = {
      data: productData,
      timestamp: Date.now()
    };
    this.saveProductCache();
  }

  // Get product from cache
  getCachedProduct(productId) {
    const cached = this.productCache[productId];
    if (cached) {
      const oneHour = 60 * 60 * 1000;
      if (Date.now() - cached.timestamp < oneHour) {
        return cached.data;
      } else {
        delete this.productCache[productId];
        this.saveProductCache();
      }
    }
    return null;
  }

  // Force refresh wishlist data from localStorage
  refreshFromStorage() {
    this.wishlistData = this.getWishlist();
    this.updateWishlistButtons();
    
    // Only trigger display update if we're on wishlist page
    const container = document.getElementById('wishlist-grid');
    if (container) {
      // Clear rendered products cache to force a clean reload
      this.renderedProducts.clear();
      this.updateWishlistDisplay();
    }
    
    return this.wishlistData;
  }

  // Observe DOM changes to update buttons when new elements are added
  observeDOM() {
    if (typeof MutationObserver === 'undefined') return;
    
    const observer = new MutationObserver((mutations) => {
      let shouldUpdateButtons = false;
      let shouldUpdateDisplay = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check for wishlist buttons
              if (node.classList?.contains('product-card__wishlist-btn') || 
                  node.querySelector?.('.product-card__wishlist-btn')) {
                shouldUpdateButtons = true;
              }
              
              // Check for complete section reloads (not individual product additions)
              if (node.classList?.contains('shopify-section') &&
                  node.querySelector?.('#wishlist-grid')) {
                shouldUpdateDisplay = true;
              }
            }
          });
        }
        
        // Watch for attribute changes on wishlist container (grid settings) only if grid is empty
        if (mutation.type === 'attributes' && 
            mutation.target.id === 'wishlist-grid' &&
            mutation.target.children.length === 0) {
          shouldUpdateDisplay = true;
        }
      });
      
      if (shouldUpdateDisplay && !this.isRendering) {
        setTimeout(() => this.updateWishlistDisplay(), 50);
      } else if (shouldUpdateButtons) {
        setTimeout(() => this.updateWishlistButtons(), 10);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'data-columns']
    });
  }

  // Save wishlist to localStorage
  saveWishlist() {
    try {
      const jsonData = JSON.stringify(this.wishlistData);
      localStorage.setItem(this.storageKey, jsonData);
    } catch (e) {
      console.error('Error saving wishlist data:', e);
    }
  }

  // Add product to wishlist
  addToWishlist(itemId, variantData = null) {
    const itemIdStr = String(itemId);
    
    if (!this.isInWishlist(itemIdStr)) {
      // Prova a ottenere l'handle del prodotto dal DOM
      const productHandle = variantData?.productHandle || this.getProductHandle(variantData?.productId || itemIdStr);
      
      const wishlistItem = {
        id: itemIdStr, // uniqueId (productId + colorVariant)
        productId: variantData?.productId || itemIdStr,
        colorVariant: variantData?.colorVariant || null, // Il colore (option1)
        variantId: variantData?.variantId || null, // La taglia specifica selezionata
        variantTitle: variantData?.variantTitle || null,
        variantImage: variantData?.variantImage || null,
        price: variantData?.price || null,
        compareAtPrice: variantData?.compareAtPrice || null,
        available: variantData?.available !== false,
        handle: productHandle,
        dateAdded: new Date().toISOString()
      };
      
      this.wishlistData.push(wishlistItem);
      this.saveWishlist();
      
      // Se siamo sulla pagina wishlist, aggiungi solo questo elemento
      if (document.getElementById('wishlist-grid')) {
        this.addSingleProductToGrid(wishlistItem);
      }
      
      this.updateWishlistButtons();
      this.updateWishlistCount();
    }
  }

  // Remove product from wishlist
  removeFromWishlist(itemId) {
    const itemIdStr = String(itemId);
    const index = this.wishlistData.findIndex(item => String(item.id) === itemIdStr);
    
    if (index > -1) {
      this.wishlistData.splice(index, 1);
      this.saveWishlist();
      
      // Se siamo sulla pagina wishlist, rimuovi solo questo elemento
      if (document.getElementById('wishlist-grid')) {
        this.removeSingleProductFromGrid(itemIdStr);
      }
      
      this.updateWishlistButtons();
      this.updateWishlistCount();
    }
  }

  // Check if product/variant is in wishlist
  isInWishlist(itemId) {
    // Converti entrambi a stringa per un confronto più sicuro
    const itemIdStr = String(itemId);
    return this.wishlistData.some(item => String(item.id) === itemIdStr);
  }

  // Update wishlist display on wishlist page
  updateWishlistDisplay() {
    const wishlistEmpty = document.getElementById('wishlist-empty');
    const wishlistProducts = document.getElementById('wishlist-products');
    const wishlistGrid = document.getElementById('wishlist-grid');

    if (!wishlistEmpty) return; // Not on wishlist page

    // Prevent re-rendering if already in progress
    if (this.isRendering) return;

    // Show/hide empty state vs products
    const count = this.wishlistData.length;
    if (count === 0) {
      wishlistEmpty.style.display = 'flex';
      wishlistProducts.style.display = 'none';
      this.renderedProducts.clear();
    } else {
      wishlistEmpty.style.display = 'none';
      wishlistProducts.style.display = 'block';
      
      // Only render if grid is empty or we have new products
      if (wishlistGrid.children.length === 0 || this.hasNewProducts()) {
        this.renderWishlistProducts(wishlistGrid);
      }
    }
    
    // Update header wishlist count if it exists (but remove badge)
    this.removeHeaderBadge();
  }

  // Check if there are new products to render
  hasNewProducts() {
    return this.wishlistData.some(item => !this.renderedProducts.has(item.id));
  }

  // Update just the wishlist count without re-rendering
  updateWishlistCount() {
    const wishlistEmpty = document.getElementById('wishlist-empty');
    const wishlistProducts = document.getElementById('wishlist-products');

    if (!wishlistEmpty) return; // Not on wishlist page

    const count = this.wishlistData.length;
    if (count === 0) {
      wishlistEmpty.style.display = 'flex';
      wishlistProducts.style.display = 'none';
      this.renderedProducts.clear();
    } else {
      wishlistEmpty.style.display = 'none';
      wishlistProducts.style.display = 'block';
    }
  }

  // Enhanced method to handle theme customizer reloads
  handleThemeCustomizerReload() {
    // Wait for DOM to be ready after section reload
    setTimeout(() => {
      if (document.getElementById('wishlist-grid')) {
        // Only force a full reload if the grid is empty (section was reloaded)
        const container = document.getElementById('wishlist-grid');
        if (container && container.children.length === 0) {
          this.renderedProducts.clear();
          this.updateWishlistDisplay();
        }
        this.updateWishlistButtons();
      }
    }, 100);
  }

  // Render products in wishlist grid with batch loading
  async renderWishlistProducts(container) {
    if (!container || this.isRendering) return;

    this.isRendering = true;
    
    // Clear only if starting fresh
    if (container.children.length === 0) {
      const template = document.getElementById('wishlist-card-template');
      
      // Show loading state only for initial load
      container.innerHTML = '<div class="wishlist-loading"><div class="loading__spinner"><svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div><p>Caricamento prodotti...</p></div>';
      
      try {
        // Fetch all products simultaneously (batch loading)
        const productPromises = this.wishlistData.map(item => this.fetchProductData(item));
        const productResults = await Promise.allSettled(productPromises);
        
        // Clear loading state
        container.innerHTML = '';
        
        // Process results
        let validProductsFound = false;
        productResults.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value) {
            validProductsFound = true;
            const wishlistItem = this.wishlistData[index];
            const productElement = this.createProductCard(template, result.value, wishlistItem);
            container.appendChild(productElement);
            this.renderedProducts.add(wishlistItem.id);
          } else {
            // Product failed to load or was removed from store
            console.warn('Failed to load product:', this.wishlistData[index], result.reason);
          }
        });
        
        // If no valid products were found, show empty state
        if (!validProductsFound) {
          this.wishlistData = []; // Clear invalid data
          this.saveWishlist();
          this.renderedProducts.clear();
          this.updateWishlistCount(); // Use count update instead of full display update
        }
        
      } catch (error) {
        console.error('Error batch loading wishlist products:', error);
        container.innerHTML = '<div class="wishlist-error"><p>Errore nel caricamento dei prodotti. <button onclick="window.wishlist.forceReload()">Riprova</button></p></div>';
      }
    }
    
    this.isRendering = false;
  }

  // Force a complete reload (for error recovery)
  forceReload() {
    const container = document.getElementById('wishlist-grid');
    if (container) {
      this.renderedProducts.clear();
      container.innerHTML = '';
      this.renderWishlistProducts(container);
    }
  }

  // Fetch product data from Shopify API with improved error handling
  async fetchProductData(productItem) {
    // Use productId for fetching, not variantId
    const productId = productItem.productId || productItem.id;
    const cachedProduct = this.getCachedProduct(productId);
    if (cachedProduct) {
      return cachedProduct;
    }

    const maxRetries = 2;
    let lastError;
    
    // Try with handle first, then with ID if handle fails
    const endpoints = [];
    if (productItem.handle) {
      endpoints.push(`/products/${productItem.handle}.js`);
    }
    endpoints.push(`/products/${productId}.js`);
    
    for (const endpoint of endpoints) {
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const response = await fetch(endpoint);
          if (!response.ok) {
            if (response.status === 404) {
              // Product not found, try next endpoint
              break;
            }
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          const productData = await response.json();
          this.cacheProduct(productId, productData);
          return productData;
        } catch (error) {
          lastError = error;
          console.warn(`Fetch attempt ${attempt + 1} failed for ${endpoint}:`, error);
          
          // Wait before retry (exponential backoff)
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 100));
          }
        }
      }
    }
    
    // All attempts failed - remove invalid product from wishlist
    console.error('All fetch attempts failed for product:', productItem, lastError);
    this.removeFromWishlist(productItem.id);
    return null;
  }

  // Add single product to grid without reloading everything
  async addSingleProductToGrid(productItem) {
    const container = document.getElementById('wishlist-grid');
    const template = document.getElementById('wishlist-card-template');
    
    if (!container || !template || this.renderedProducts.has(productItem.id)) return;
    
    try {
      const productData = await this.fetchProductData(productItem);
      if (productData) {
        const productElement = this.createProductCard(template, productData, productItem);
        
        // Add with smooth animation
        productElement.style.opacity = '0';
        productElement.style.transform = 'scale(0.8)';
        container.appendChild(productElement);
        
        // Animate in
        requestAnimationFrame(() => {
          productElement.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
          productElement.style.opacity = '1';
          productElement.style.transform = 'scale(1)';
        });
        
        this.renderedProducts.add(productItem.id);
      }
    } catch (error) {
      console.error('Error adding single product to grid:', error);
    }
  }

  // Remove single product from grid without reloading everything
  removeSingleProductFromGrid(productId) {
    const container = document.getElementById('wishlist-grid');
    if (!container) return;
    
    const productElement = container.querySelector(`[data-product-id="${productId}"]`);
    if (productElement) {
      // Animate out and remove
      this.animateRemoval(productElement, () => {
        if (productElement.parentNode) {
          productElement.parentNode.removeChild(productElement);
        }
        this.renderedProducts.delete(productId);
      });
    }
  }

  // Create product card element
  createProductCard(template, product, wishlistItem = null) {
    const clone = template.content.cloneNode(true);
    const listItem = clone.querySelector('.wishlist-item');
    const card = clone.querySelector('.wishlist-card');
    
    // Use uniqueId (productId + colorVariant) from wishlistItem
    const itemId = wishlistItem?.id || product.id;
    listItem.dataset.productId = itemId;
    
    // Get variant-specific data if available
    let displayImage = product.featured_image;
    let displayTitle = product.title; // SOLO nome prodotto, senza variante
    let displayPrice = product.price;
    let displayComparePrice = product.compare_at_price;
    
    // If we have variant data saved, use it
    if (wishlistItem?.variantId && wishlistItem?.variantImage) {
      displayImage = wishlistItem.variantImage;
    }
    
    // NON aggiungere variantTitle al displayTitle - mostriamo solo il nome del prodotto
    
    if (wishlistItem?.price !== null && wishlistItem?.price !== undefined) {
      displayPrice = wishlistItem.price;
    }
    
    if (wishlistItem?.compareAtPrice) {
      displayComparePrice = wishlistItem.compareAtPrice;
    }
    
    // Costruisci URL con variante se disponibile
    let productUrl = `/products/${product.handle}`;
    if (wishlistItem?.variantId) {
      productUrl += `?variant=${wishlistItem.variantId}`;
    }
    
    // Image
    const imageLink = clone.querySelector('.wishlist-card__image-link');
    const image = clone.querySelector('.wishlist-card__image');
    if (displayImage) {
      imageLink.href = productUrl;
      image.src = displayImage;
      image.alt = displayTitle;
    }

    // Title
    const titleLink = clone.querySelector('.wishlist-card__title-link');
    titleLink.href = productUrl;
    titleLink.textContent = displayTitle;

    // Price
    const priceRegular = clone.querySelector('.wishlist-card__price--regular');
    const priceCompare = clone.querySelector('.wishlist-card__price--compare');
    
    if (displayComparePrice && displayComparePrice > displayPrice) {
      priceRegular.textContent = this.formatMoney(displayPrice);
      priceCompare.textContent = this.formatMoney(displayComparePrice);
      priceCompare.style.display = 'block';
    } else {
      priceRegular.textContent = this.formatMoney(displayPrice);
      priceCompare.style.display = 'none';
    }

    // Variants and add to cart functionality
    this.setupProductVariants(clone, product, wishlistItem);

    // Remove button
    const removeBtn = clone.querySelector('.wishlist-card__remove-btn');
    removeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.animateRemoval(listItem, () => {
        this.removeFromWishlist(itemId);
      });
    });

    return clone;
  }

  // Setup product variants and add to cart
  setupProductVariants(cardElement, product, wishlistItem = null) {
    const variantsContainer = cardElement.querySelector('.wishlist-card__variants');
    const variantsGrid = cardElement.querySelector('.wishlist-card__variants-grid');
    const addToCartBtn = cardElement.querySelector('.wishlist-card__add-to-cart-btn');

    if (product.variants.length === 1) {
      // Single variant - direct add to cart
      const variant = product.variants[0];
      variantsContainer.style.display = 'none';

      if (variant.available) {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = 'Aggiungi al carrello';
        addToCartBtn.addEventListener('click', () => {
          this.addToCart(variant.id, cardElement);
        });
      } else {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Non disponibile';
      }
    } else {
      // Multiple variants - check if any are available
      const hasAvailableVariants = product.variants.some(variant => variant.available);
      
      if (hasAvailableVariants) {
        // Has available variants - open popup for product selection
        addToCartBtn.textContent = 'Aggiungi al carrello';
        addToCartBtn.disabled = false;
        addToCartBtn.classList.add('variant-selection-mode');
        
        // Pass product data to button for popup
        addToCartBtn.dataset.productData = JSON.stringify(product);
        
        // Pass the variant ID from wishlist to preselect in popup
        if (wishlistItem?.variantId) {
          addToCartBtn.dataset.preselectedVariantId = wishlistItem.variantId;
        }
        
        // Add manual click handler to ensure popup works with product data
        addToCartBtn.addEventListener('click', async (e) => {
          e.preventDefault();
          
          // Set product data for popup system
          const productData = e.target.dataset.productData;
          const preselectedVariantId = e.target.dataset.preselectedVariantId;
          
          if (productData) {
            try {
              const product = JSON.parse(productData);
              window.currentProductData = product;
              window.currentProductId = null; // Clear ID to use data instead
              
              // Set the variant to preselect in the popup
              if (preselectedVariantId) {
                window.currentPreselectedVariantId = preselectedVariantId;
              }
              
              // Load popup content first
              const popupContainer = document.getElementById('product-quick-view-modal');
              if (popupContainer && popupContainer._popupInstance) {
                // Update popup content before opening
                if (typeof window.updateProductPopupWithData === 'function') {
                  window.updateProductPopupWithData(product);
                  
                  // Wait a bit for content to render, then open
                  setTimeout(() => {
                    popupContainer._popupInstance.open();
                  }, 100);
                } else {
                  // Fallback: open immediately
                  popupContainer._popupInstance.open();
                }
              }
            } catch (error) {
              console.error('Error parsing product data:', error);
            }
          }
        });
      } else {
        // No available variants - show notify me
        addToCartBtn.textContent = 'Avvisami';
        addToCartBtn.disabled = false; // Enable for click but style as notify
        addToCartBtn.classList.add('notify-mode');
        // Remove variant selection mode if previously set
        addToCartBtn.classList.remove('variant-selection-mode');
        
        // Optional: Add click handler for notify functionality
        addToCartBtn.addEventListener('click', (e) => {
          e.preventDefault();
          // Here you could implement notify me functionality
          alert('Ti avviseremo quando il prodotto sarà nuovamente disponibile!');
        });
      }
    }
  }


  // Add product to Shopify cart - USA LA STESSA LOGICA DI product-form.js
  async addToCart(variantId, cardElement) {
    const listItem = cardElement.closest('.wishlist-item');
    listItem.classList.add('adding-to-cart');

    // Ottieni riferimento al cart drawer o cart notification (come in product-form.js:11)
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    try {
      // Prepara FormData con le sections da aggiornare (come in product-form.js:34-43)
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      if (cart) {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }

      // Fetch config (come in product-form.js:30-32)
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      const responseData = await response.json();

      if (responseData.status) {
        // Errore (come in product-form.js:48-63)
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'wishlist',
          productVariantId: variantId,
          errors: responseData.errors || responseData.description,
          message: responseData.message,
        });

        // Mostra toast di errore (come in product-card.js)
        this.showErrorToast(responseData.description || 'Errore aggiunta al carrello');
        return;
      }

      if (!cart) {
        // Se non c'è cart drawer/notification, redirect al carrello
        window.location = window.routes.cart_url;
        return;
      }

      // Pubblica evento cartUpdate (come in product-form.js:71-77)
      // La key viene ritornata direttamente da Shopify in responseData quando si includono le sections
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'wishlist',
        productVariantId: variantId,
        cartData: responseData,
      });

      // Renderizza il cart drawer/notification (come in product-form.js:94-96)
      // Questo aggiorna automaticamente il contatore E apre il drawer/notification
      cart.renderContents(responseData);

      // Remove from wishlist after successful add to cart
      setTimeout(() => {
        this.animateRemoval(listItem, () => {
          const productId = listItem.dataset.productId;
          this.removeFromWishlist(productId);
        });
      }, 500);

    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      listItem.classList.remove('adding-to-cart');
    }
  }

  // Animate item removal
  animateRemoval(element, callback) {
    element.classList.add('removing');
    setTimeout(() => {
      callback();
    }, 300);
  }

  // Show error toast (same as product-card.js)
  showErrorToast(message) {
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #e74c3c;
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 9999;
      font-family: var(--font-muli);
      font-size: 14px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
    `;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.transition = 'opacity 0.3s ease';
      toast.style.opacity = '0';
      setTimeout(() => {
        toast.remove();
      }, 300);
    }, 3000);
  }

  // Bind event listeners
  bindEvents() {
    // Wishlist buttons on product cards
    document.addEventListener('click', (e) => {
      if (e.target.closest('.product-card__wishlist-btn')) {
        e.preventDefault();
        const button = e.target.closest('.product-card__wishlist-btn');
        const card = button.closest('.product-card');
        
        // Get variant data from the card
        // useFallback=true: se non c'è taglia selezionata, usa la prima disponibile del colore
        const variantData = this.getVariantDataFromCard(card, button, true);
        
        // Use uniqueId (productId + colorVariant) as the identifier
        const itemId = variantData.uniqueId;
        
        if (this.isInWishlist(itemId)) {
          this.removeFromWishlist(itemId);
        } else {
          this.addToWishlist(itemId, variantData);
        }
      }
    });
  }
  
  // Get variant data from product card
  // useFallback: se true, usa la prima variante disponibile se non selezionata (per ADD)
  //              se false, NON usa fallback (per CHECK - cuoricino attivo solo se variante esatta è selezionata)
  getVariantDataFromCard(card, button, useFallback = true) {
    const productId = button.dataset.productId;
    const productHandle = button.dataset.productHandle;
    const colorVariant = button.dataset.colorVariant || card?.dataset.colorVariant; // Il colore della card (option1)
    
    // Try to get product data from card
    let productData = null;
    try {
      if (card && card.dataset.productData) {
        productData = JSON.parse(card.dataset.productData);
      }
    } catch (e) {
      console.error('Error parsing product data:', e);
    }
    
    // Get selected variant ID from form if available (per la taglia)
    const variantInput = card?.querySelector('.product-card__variant-input');
    let selectedVariantId = variantInput?.value;
    
    // Find the variant object
    let variant = null;
    if (productData && productData.variants) {
      if (selectedVariantId) {
        // Use selected variant (taglia specifica)
        variant = productData.variants.find(v => String(v.id) === String(selectedVariantId));
      }
      
      // Fall back to first available variant DEL COLORE CORRENTE se useFallback è true
      if (!variant && useFallback && colorVariant) {
        variant = productData.variants.find(v => v.option1 === colorVariant && v.available) 
                  || productData.variants.find(v => v.option1 === colorVariant);
      }
      
      // Ultimo fallback generico se non c'è colore
      if (!variant && useFallback) {
        variant = productData.variants.find(v => v.available) || productData.variants[0];
      }
    }
    
    // Get variant image (variant-specific or product featured image)
    let variantImage = null;
    if (variant && productData) {
      // Try to find variant-specific image
      const variantMedia = productData.media?.find(m => 
        m.variant_ids && m.variant_ids.includes(variant.id)
      );
      
      if (variantMedia) {
        variantImage = variantMedia.url;
      } else if (productData.featured_media) {
        variantImage = productData.featured_media.url;
      }
    }
    
    // L'ID univoco è: productId + colorVariant (NON include la taglia)
    const uniqueId = colorVariant ? `${productId}-${colorVariant}` : productId;
    
    return {
      productId: productId,
      productHandle: productHandle,
      colorVariant: colorVariant, // Il colore (option1)
      uniqueId: uniqueId, // productId + colore
      variantId: variant?.id || null, // La variante specifica con taglia (solo per add to cart)
      variantTitle: variant?.title || null,
      variantImage: variantImage,
      price: variant?.price || null,
      compareAtPrice: variant?.compare_at_price || null,
      available: variant?.available || false
    };
  }

  // Update wishlist button states throughout the site
  updateWishlistButtons() {
    const buttons = document.querySelectorAll('.product-card__wishlist-btn');
    
    if (buttons.length === 0) {
      return;
    }
    
    buttons.forEach((button) => {
      const card = button.closest('.product-card');
      const icon = button.querySelector('svg');
      
      // Get variant data (usa fallback per avere sempre un uniqueId valido)
      const variantData = this.getVariantDataFromCard(card, button, true);
      
      // Controlla se questo prodotto+colore è nella wishlist
      const isInWishlist = this.isInWishlist(variantData.uniqueId);
      
      if (isInWishlist) {
        button.classList.add('in-wishlist');
        button.setAttribute('aria-label', 'Rimuovi dalla lista dei desideri');
        if (icon) {
          icon.style.fill = 'currentColor';
          icon.style.stroke = 'currentColor';
        }
      } else {
        button.classList.remove('in-wishlist');
        button.setAttribute('aria-label', 'Aggiungi alla lista dei desideri');
        if (icon) {
          icon.style.fill = 'none';
          icon.style.stroke = 'currentColor';
        }
      }
    });
  }

  // Format money
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return `€ ${amount}`;
  }

  // Get wishlist count
  getWishlistCount() {
    return this.wishlistData.length;
  }

  // Get wishlist products
  getWishlistProducts() {
    return this.wishlistData;
  }

  // Get product handle from DOM context
  getProductHandle(productId) {
    // Prima prova a trovare l'handle dal link del prodotto nella stessa card
    const button = document.querySelector(`[data-product-id="${productId}"]`);
    if (button) {
      const card = button.closest('.product-card, .card-wrapper, .grid__item');
      if (card) {
        // Cerca un link al prodotto nella card
        const productLink = card.querySelector('a[href*="/products/"]');
        if (productLink) {
          const href = productLink.getAttribute('href');
          const handleMatch = href.match(/\/products\/([^?#\/]+)/);
          if (handleMatch) {
            return handleMatch[1];
          }
        }
      }
    }

    // Se siamo su una pagina prodotto, prova a ottenere l'handle dalla URL
    if (window.location.pathname.includes('/products/')) {
      const pathMatch = window.location.pathname.match(/\/products\/([^?#\/]+)/);
      if (pathMatch) {
        return pathMatch[1];
      }
    }

    // Ultima risorsa: cerca qualsiasi link prodotto nella pagina con questo ID
    const allProductLinks = document.querySelectorAll('a[href*="/products/"]');
    for (const link of allProductLinks) {
      // Verifica se il link è associato a questo prodotto ID
      const nearestButton = link.closest('.product-card, .card-wrapper, .grid__item')?.querySelector(`[data-product-id="${productId}"]`);
      if (nearestButton) {
        const href = link.getAttribute('href');
        const handleMatch = href.match(/\/products\/([^?#\/]+)/);
        if (handleMatch) {
          return handleMatch[1];
        }
      }
    }

    console.warn('Could not find product handle for ID:', productId);
    return null;
  }

  // Remove header badge (we don't want counters)
  removeHeaderBadge() {
    const headerWishlistLink = document.querySelector('a[href*="wishlist"]');
    if (headerWishlistLink) {
      const badge = headerWishlistLink.querySelector('.wishlist-badge');
      if (badge) {
        badge.remove();
      }
      // Reset position in case it was changed
      headerWishlistLink.style.position = '';
    }
  }
}

// Initialize wishlist when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.wishlist = new Wishlist();
  
  // Listen for localStorage changes in other tabs
  window.addEventListener('storage', (e) => {
    if (e.key === 'vanacore_wishlist') {
      window.wishlist.refreshFromStorage();
    }
  });
  
  // Listen for Shopify theme editor events to prevent wishlist disappearance
  if (window.Shopify && window.Shopify.designMode) {
    // When sections are reloaded in the theme editor
    document.addEventListener('shopify:section:load', (event) => {
      // Only reinitialize if we're on the wishlist page
      if (document.getElementById('wishlist-grid')) {
        window.wishlist.handleThemeCustomizerReload();
      }
    });
    
    // When sections are reordered
    document.addEventListener('shopify:section:reorder', () => {
      if (document.getElementById('wishlist-grid')) {
        window.wishlist.handleThemeCustomizerReload();
      }
    });
    
    // When section settings are changed
    document.addEventListener('shopify:section:select', () => {
      if (document.getElementById('wishlist-grid')) {
        window.wishlist.handleThemeCustomizerReload();
      }
    });
    
    // When sections are unloaded (important for cleanup)
    document.addEventListener('shopify:section:unload', (event) => {
      // If wishlist section is being unloaded, we'll need to reinitialize when it loads again
      if (event.detail.sectionId && window.wishlist) {
        window.wishlist.updateWishlistButtons();
      }
    });
  }
});

// Backup: initialize when window loads
window.addEventListener('load', () => {
  if (!window.wishlist) {
    window.wishlist = new Wishlist();
  }
});

// Additional event listener for theme customizer changes
window.addEventListener('resize', () => {
  // Debounce resize events to avoid too many calls
  if (window.wishlistResizeTimeout) {
    clearTimeout(window.wishlistResizeTimeout);
  }
  window.wishlistResizeTimeout = setTimeout(() => {
    if (window.wishlist && document.getElementById('wishlist-grid')) {
      window.wishlist.updateWishlistDisplay();
    }
  }, 250);
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = Wishlist;
}
